import { useAnalyticsTracking } from "@configs/analytics/useAnalyticsTracking";
import { STORAGE_KEY } from "@constants";
import { PWA_NOTIFICATION_SNOOZE_PREFIX } from "@hooks/useNotificationHandler";
import type { AppDispatch } from "@stores";
import preferenceSlice from "@stores/preference/slices";
import { getRuntimeContext } from "@utils/pwa";
import type { BeforeInstallPromptEvent } from "kz-ui-sdk";
import { getPersistedDueAt } from "kz-ui-sdk";
import moment from "moment";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

/**
 * Captures the PWA `beforeinstallprompt` event and syncs installability + deferred prompt into Redux.
 * Headless; keep alongside AppUpdateDetector (same class of global browser hooks).
 */
const PwaInstallInterceptor = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { trackPwaInstallSuccess } = useAnalyticsTracking();

  useEffect(() => {
    const beforeInstallPromptHandler = (event: Event) => {
      event.preventDefault();
      const dueAt = getPersistedDueAt(STORAGE_KEY.PWA_REMINDER);
      if (dueAt && !moment().isAfter(dueAt)) {
        return;
      }
      sessionStorage.setItem(STORAGE_KEY.BEFORE_INSTALL_PROMPT_FIRED, "true");
      dispatch(preferenceSlice.actions.setIsAppInstallable(true));
      dispatch(preferenceSlice.actions.setAppInstallerPrompt(event as BeforeInstallPromptEvent));
    };

    window.addEventListener("beforeinstallprompt", beforeInstallPromptHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstallPromptHandler);
    };
  }, [dispatch]);

  useEffect(() => {
    // localStorage persists across PWA uninstall on most platforms, so a dismiss from a
    // previous install keeps blocking the drawer after reinstall. Clear the PWA-scoped
    // snooze on install so the new install gets a fresh prompt.
    const handleAppInstalled = () => {
      trackPwaInstallSuccess("android_appinstalled");
      Object.keys(localStorage)
        .filter((k) => k.startsWith(PWA_NOTIFICATION_SNOOZE_PREFIX))
        .forEach((k) => localStorage.removeItem(k));
    };
    window.addEventListener("appinstalled", handleAppInstalled);
    return () => {
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [trackPwaInstallSuccess]);

  useEffect(() => {
    // iOS / external have no install event; the first standalone launch is the proxy.
    if (getRuntimeContext() === "pwa") {
      trackPwaInstallSuccess("first_standalone_open");
    }
  }, [trackPwaInstallSuccess]);

  return null;
};

export default PwaInstallInterceptor;
